import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect, useContext } from "react";
import { useBreadcrumb } from "../../../context/BreadcrumbContext";
import TopicService from "../../../services/TopicService";
import AuthContext from "../../../auth/AuthContext";
import AlertService from "../../../services/AlertService";

const SubTopicVideos = () => {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const { setDynamicLabels } = useBreadcrumb();
  const authContext = useContext(AuthContext);

  const [topic, setTopic] = useState(null);
  const [subTopics, setSubTopics] = useState([]);
  const [loading, setLoading] = useState(true);

  const progressPercent = 0;

  useEffect(() => {
    fetchTopicDetails();
  }, [topicId]);

  const fetchTopicDetails = async () => {
    try {
      setLoading(true);

      const topicRes = await TopicService.GetTopicById(topicId, authContext);
      if (topicRes?.data) setTopic(topicRes.data);

      const subRes = await TopicService.GetAllSubTopicsByTopicId(
        topicId,
        authContext,
      );

      const formatted = await Promise.all(
        (subRes?.data || [])
          .filter((st) => !st.isDeleted)
          .map(async (st) => {
            const videoRes = await TopicService.GetAllVideosBySubTopicId(
              st.id,
              authContext,
            );

            return {
              subTopicId: st.id,
              subTopicTitle: st.name,
              videos:
                videoRes?.data?.map((v) => ({
                  
                  videoId: v.id,
                  title: v.name,
                  duration: `${v.duration || 0} mins`,
                  thumbnailUrl: v.thumbnailUrl,
                })) || [],
            };
          }),
      );

      setSubTopics(formatted);
    } catch (err) {
      console.error(err);
      AlertService.error("Failed to load topic data");
    } finally {
      setLoading(false);
    }
  };

  /* BREADCRUMB */
  useEffect(() => {
    if (topic) {
      setDynamicLabels({
        topicId,
        topicTitle: topic.name,
      });
    }
  }, [topic, topicId]);

 if (loading) {
  return (
    <div className="space-y-8 pt-4">
      {Array.from({ length: 2 }).map((_, sectionIndex) => (
        <div key={sectionIndex}>
          <div className="h-5 w-40 mb-4 rounded bg-slate-200 dark:bg-slate-700 animate-pulse" />
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {Array.from({ length: 4 }).map((_, cardIndex) => (
              <div
                key={cardIndex}
                className="
                  bg-white dark:bg-slate-800
                  border border-gray-200 dark:border-slate-700
                  rounded-xl shadow-sm
                  animate-pulse
                  overflow-hidden
                "
              >
                <div className="w-full h-56 bg-slate-200 dark:bg-slate-700" />
                <div className="p-3">
                  <div className="h-4 w-3/4 rounded bg-slate-200 dark:bg-slate-700" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}


  if (!topic) {
    return <div className="text-center text-slate-500">Topic not found</div>;
  }

  return (
    <div className="space-y-8">
      {topic.hasAssessment && (
        <div className="flex justify-end">
          <div className="flex items-center gap-4 w-full max-w-xl">
            <div className="flex-1">
              <div className="flex justify-between text-xs mb-1">
                <span>Learning</span>
                <span>{progressPercent}%</span>
              </div>

              <div className="h-2 bg-slate-200 rounded overflow-hidden">
                <div
                  className="h-2 bg-green-600 transition-all"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>

            <button
              onClick={() =>
                navigate(`/knowledge-management/topics/${topicId}/assessment`)
              }
              className="px-4 py-2 rounded text-white bg-[#0e3a46]"
            >
              Take Assessment
            </button>
          </div>
        </div>
      )}

      {/* SUB TOPICS */}
      {subTopics.map((sub) => (
        <div key={sub.subTopicId}>
          <h2 className="mb-4 text-lg font-medium">{sub.subTopicTitle}</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {sub.videos.map((video) => (
              <div
                key={video.videoId}
                onClick={() =>
                  navigate(
                    `/knowledge-management/topics/${topicId}/video/${video.videoId}`,
                  )
                }
                className="
                  bg-white dark:bg-slate-900 border dark:border-slate-700 rounded-xl shadow-sm
                  cursor-pointer hover:shadow-md transition
                "
              >
<img
  src={video.thumbnailUrl}
  alt={video.title}
  className="w-full h-56 object-cover rounded-t-xl"
  referrerPolicy="no-referrer"
  loading="lazy"
/>



                <div className="p-3 flex justify-start text-sm">
                  <span className="truncate">{video.title}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default SubTopicVideos;
